import React, { useState } from "react";
import { toast } from "react-toastify";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { PrimaryButton } from "../../UI";
import ConfirmModal from "../../UI/Modal/confirmModal";
import { IconButton, InfoTip, JsonEditor, LinkButton, optionsTip, SelectInput } from "./common";
import { createSource, RELATION_LABELS } from "./constants";
import { explain, GLOSSARY } from "./glossary";
import { moveItem } from "./helpers";
import SourceDrawer from "./source/SourceDrawer";

const requestExtras = (request = {}) => {
    const extras = [];
    if (request.pagination?.type && request.pagination.type !== "none") {
        extras.push({ text: `${request.pagination.type} pagination`, tip: GLOSSARY.request.paginationTypes[request.pagination.type] });
    }
    if (request.repeat_for?.length) extras.push({ text: `${request.repeat_for.length} repeated calls`, tip: GLOSSARY.request.repeatFor });
    if (request.for_each?.source) extras.push({ text: `one call per value of ${request.for_each.source}`, tip: GLOSSARY.sources.forEach });
    return extras;
};

const matchOnTip = (matchOn) => explain(GLOSSARY.sources.matchOn, matchOn) || GLOSSARY.sources.matchOn["variant.field"];

const INTRO_TIP = optionsTip(
    undefined,
    { order: GLOSSARY.sources.order, merge: GLOSSARY.fields.merge.priority, primary: GLOSSARY.sources.roles.primary, enrichment: GLOSSARY.sources.roles.enrichment },
    { order: "Priority order", merge: "Merge policy (default)", primary: "Primary", enrichment: "Enrichment" }
);

// Everything a source card shows, explained in one place.
const SourceCardTip = ({ source, required, deps, extras, relation }) => {
    const matchOn = source.enrichment?.match_on || "product.key";
    const rows = [
        [source.role === "primary" ? "Primary" : "Enrichment", explain(GLOSSARY.sources.roles, source.role)],
        [required ? "Required" : "Optional", required ? GLOSSARY.sources.required : GLOSSARY.sources.optional],
        ...(deps.length ? [[`← ${deps.join(", ")}`, GLOSSARY.sources.dependsOn]] : []),
        ...extras.map((extra) => [extra.text, extra.tip]),
        [RELATION_LABELS[relation] || relation, explain(GLOSSARY.relations, relation)],
        ["Mapped fields", GLOSSARY.sources.mapped],
        ...(source.role === "enrichment" ? [[`Matches on ${matchOn}`, matchOnTip(matchOn)]] : []),
    ];
    return (
        <div className="flex flex-col gap-1">
            {rows.map(([term, text]) => (
                <p key={term}>
                    <span className="font-semibold">{term}:</span> {text}
                </p>
            ))}
        </div>
    );
};

const dependenciesOf = (source) => {
    const ids = new Set();
    if (source.request?.for_each?.source) ids.add(source.request.for_each.source);
    JSON.stringify([source.product_map, source.variant_map], (key, value) => {
        if (key === "source_lookup" && value?.source) ids.add(value.source);
        return value;
    });
    return [...ids];
};

// Sources tab: source cards in priority order.
const SourcesTab = ({ draft, updateDraft, feed, auth, meta, feedId, dirty, saveDraft }) => {
    const [editing, setEditing] = useState(null);
    const [removing, setRemoving] = useState(null);
    const [preset, setPreset] = useState("nested");
    const [showJson, setShowJson] = useState(false);
    const sources = draft.sources || [];
    const productFields = (draft.model?.product_fields || []).map((f) => f.key);
    const variantFields = (draft.model?.variant_fields || []).map((f) => f.key);

    const setSources = (next) => updateDraft((d) => ({ ...d, sources: next }));

    const addSource = () => {
        const source = createSource(sources, meta?.source_presets?.[preset]);
        setSources([...sources, source]);
        setEditing(sources.length);
    };

    const presetOptions = Object.entries(meta?.source_presets || {}).map(([value, p]) => ({ value, label: p.label }));

    return (
        <div className="flex flex-col gap-4 mt-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <p className="text-sm text-gray-600 max-w-3xl">
                    Sources are read in priority order: for each field, the first source with a value wins unless the field's merge policy says otherwise.
                    Primary sources create products; enrichment sources only add to products that already exist. <InfoTip content={INTRO_TIP} wide />
                </p>
                <div className="flex items-center gap-2">
                    <InfoTip content={GLOSSARY.sources.presets} />
                    <SelectInput value={preset} onChange={setPreset} options={presetOptions} className="min-w-[230px]" />
                    <PrimaryButton size="sm" handleClick={addSource}>
                        <Plus size={16} className="mr-1" /> Add source
                    </PrimaryButton>
                </div>
            </div>

            {sources.length === 0 && <p className="text-sm text-gray-500 py-8 text-center border border-dashed border-gray-300 rounded-lg">No sources yet. Add the product API this feed reads from.</p>}

            {sources.map((source, index) => {
                const relation = source.extract?.variants?.relation?.type || (source.extract?.products?.group_by ? "group" : "self");
                const mappedProduct = productFields.filter((key) => source.product_map?.[key]).length;
                const mappedVariant = variantFields.filter((key) => source.variant_map?.[key]).length;
                const deps = dependenciesOf(source);
                const extras = requestExtras(source.request);
                const required = source.failure?.required ?? source.role === "primary";
                return (
                    <div key={source.id} className="border border-gray-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
                        <div className="flex flex-col gap-1 min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-mono text-gray-400">#{index + 1}</span>
                                <button type="button" className="text-base font-semibold text-primary hover:underline" onClick={() => setEditing(index)}>
                                    {source.name || source.id}
                                </button>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${source.role === "primary" ? "bg-blue-100 text-blue-800" : "bg-purple-100 text-purple-800"}`}>{source.role}</span>
                                <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{required ? "required" : "optional"}</span>
                                {deps.length > 0 && <span className="text-xs text-gray-500">← {deps.join(", ")}</span>}
                                <InfoTip content={<SourceCardTip source={source} required={required} deps={deps} extras={extras} relation={relation} />} wide />
                            </div>
                            <p className="text-xs text-gray-600 font-mono flex min-w-0">
                                <span className="truncate" title={`${source.request?.method || "GET"} ${source.request?.url || ""}`}>
                                    {source.request?.method || "GET"} {source.request?.url || "(no URL)"}
                                </span>
                                {extras.map((extra) => (
                                    <span key={extra.text} className="shrink-0 whitespace-nowrap">
                                        &nbsp;·&nbsp;{extra.text}
                                    </span>
                                ))}
                            </p>
                            <p className="text-xs text-gray-500">
                                {RELATION_LABELS[relation] || relation} · mapped {mappedProduct}/{productFields.length} product and {mappedVariant}/{variantFields.length} variant fields
                                {source.role === "enrichment" && ` · matches on ${source.enrichment?.match_on || "product.key"}`}
                            </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                            <IconButton title="Move up" disabled={index === 0} onClick={() => setSources(moveItem(sources, index, index - 1))}>
                                <ArrowUp size={16} />
                            </IconButton>
                            <IconButton title="Move down" disabled={index === sources.length - 1} onClick={() => setSources(moveItem(sources, index, index + 1))}>
                                <ArrowDown size={16} />
                            </IconButton>
                            <IconButton title="Edit" onClick={() => setEditing(index)}>
                                <Pencil size={16} />
                            </IconButton>
                            <IconButton title="Remove" onClick={() => setRemoving(index)}>
                                <Trash2 size={16} />
                            </IconButton>
                        </div>
                    </div>
                );
            })}

            <LinkButton onClick={() => setShowJson(!showJson)}>{showJson ? "Hide" : "Edit"} sources as JSON</LinkButton>
            {showJson && <JsonEditor value={sources} onChange={(value) => Array.isArray(value) && setSources(value)} rows={20} />}

            {editing !== null && sources[editing] && (
                <SourceDrawer
                    source={sources[editing]}
                    sources={sources}
                    draft={draft}
                    auth={auth}
                    meta={meta}
                    feedId={feedId}
                    feed={feed}
                    dirty={dirty}
                    saveDraft={saveDraft}
                    onApply={(next) => setSources(sources.map((s, i) => (i === editing ? next : s)))}
                    onModelChange={(model) => updateDraft((d) => ({ ...d, model }))}
                    onClose={() => setEditing(null)}
                />
            )}

            {removing !== null && (
                <ConfirmModal
                    title="Remove source"
                    ConfirmText="Remove"
                    description={`Remove "${sources[removing]?.name || sources[removing]?.id}" from this feed? The change is saved with the draft.`}
                    hideModal={() => setRemoving(null)}
                    handleConfirmation={() => {
                        toast.info(`"${sources[removing]?.name || sources[removing]?.id}" removed. Save the draft to keep the change.`);
                        setSources(sources.filter((_, i) => i !== removing));
                        setRemoving(null);
                    }}
                />
            )}
        </div>
    );
};

export default SourcesTab;
